/**
 * ESTABLISHMENT-CONTRIBUTORS
 * Edit list of contributors for the establishment
 */
import { useState } from "react"
import { store, useAppDispatch, useAppSelector } from "@/core/root-reducer"
import { t } from "i18next"
import {
  faCopy,
  faCrown,
  faEdit,
  faEllipsisV,
  faUserCircle,
  faUserMinus,
  faUserPlus,
  faUsers
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import ListButton from "@/components/list-button"
import Link from "@/components/link"
import { User } from "@/redux/user.types"
import ListItem from "@/components/list-item"
import ListIcon from "@/components/list-icon"
import Space from "@/components/space"
import SendInvitationButton from "@/components/SendInvitationButton"
import copyLink from "@/utils/copy-link.utils"
import AdmSendEmailModal from "@/modals/adm-send-email.modal"
import AdmEditUserModal from "@/modals/adm-edit-user.modal"
import { toast } from "react-toastify"
import establishmentStore from "../model/establishment.store"
import Card from "@/components/card"
import Chip from "@/components/chip"
import { userGet, userUpdate } from "@/redux/user.actions"
import { Establishment } from "../model/establishment.types"
import { STATUS_LOADED } from "@/redux/_status.types"
import { pick } from "lodash"

const ACTION_USER_COPY_LINK = "ACTION_USER_COPY_LINK"
const ACTION_USER_EDIT = "ACTION_USER_EDIT"
const ACTION_USER_INVITE = "ACTION_USER_INVITE"
const ACTION_USER_REMOVE = "ACTION_USER_REMOVE"

const MODAL_SEND_EMAIL = "MODAL_SEND_EMAIL"
const MODAL_USER_ADD = "MODAL_USER_ADD"
const MODAL_USER_EDIT = "MODAL_USER_EDIT"

interface Props {
  establishment: Establishment
}

export default function EstablishmentContributors({ establishment }: Props) {
  const dispatch = useAppDispatch()

  //Selectors
  const _session = useAppSelector((state) => state._session)
  const status = useAppSelector(establishmentStore.selectors.status)
  const accountRisksSettings = useAppSelector((state) => state._session.accountRisksSettings)
  const users = useAppSelector((state) => state.user.list)
  const usersForEstablishment = users.filter((user) => user.EstablishmentId === establishment.id)
  const contributors =
    users.length > 0
      ? usersForEstablishment
      : (establishment.Users ?? []).filter((user) => user.EstablishmentId === establishment.id)

  //State
  const [currentUser, setCurrentUser] = useState(new User())
  const [modal, setModal] = useState<null | string>(null)

  //Trigger specific action
  function clickAction(action: string, user: User) {
    const establishmentUser = new User({ ...user })
    setCurrentUser(establishmentUser)

    switch (action) {
      case ACTION_USER_COPY_LINK:
        return copyLink(new User(pick(user, ["id", "email", "firstname", "lastname"])), _session)
      case ACTION_USER_EDIT:
        return setModal(MODAL_USER_EDIT)
      case ACTION_USER_INVITE:
        return setModal(MODAL_SEND_EMAIL)
      case ACTION_USER_REMOVE:
        return save(establishmentUser, "remove")
      default:
        return null
    }
  }

  //Init actions
  function getActions() {
    const actions = [
      {
        id: ACTION_USER_EDIT,
        icon: faEdit,
        label: t("Modifier le collaborateur")
      }
    ]

    actions.push({
      id: ACTION_USER_COPY_LINK,
      icon: faCopy,
      label: t("Copier le lien d'invitation")
    })

    actions.push({
      id: ACTION_USER_REMOVE,
      icon: faUserMinus,
      label: t(`Retirer de la liste des contributeurs`)
    })
    return actions
  }

  //Open the modal email if allowed
  function openSendEmailModal(user: User) {
    setCurrentUser(user)
    setModal(user.email ? MODAL_SEND_EMAIL : null)
  }

  function getContributorAccess() {
    return { EstablishmentId: establishment.id }
  }

  //Save changes
  async function save(user: User, operation: "add" | "edit" | "remove") {
    const contributorAccess = getContributorAccess()

    // add
    if (operation === "add") {
      if (contributors.some((u) => u.id === user.id)) {
        toast(t("Ce collaborateur est déjà rattaché."))
        return
      }
      const updatedUser = new User({ ...user, ...contributorAccess })

      await store.dispatch(userUpdate(updatedUser, true))
      toast(t("Contributeur ajouté à l'établissement."))
      setModal(null)
      openSendEmailModal(updatedUser)
    }

    // edit
    if (operation === "edit") {
      await store.dispatch(userUpdate(new User({ ...user, ...contributorAccess }), true))
      toast(t("Contributeur mis à jour."))
      setModal(null)
    }

    // remove
    if (operation === "remove") {
      store.dispatch(
        userUpdate(
          {
            ...user,
            AccountId: _session.accountId,
            Establishment: null,
            EstablishmentId: null
          },
          false
        )
      )
      toast(t("Contributeur retiré de l'établissement."))
    }

    const updatedUser = new User({
      ...user,
      ...(operation === "remove" ? { Establishment: null, EstablishmentId: null } : contributorAccess)
    })
    const nextUsers = users.some((u) => u.id === updatedUser.id)
      ? users.map((u) => (u.id === updatedUser.id ? updatedUser : u))
      : [updatedUser].concat(users)
    dispatch(userGet(nextUsers))
  }

  //Component Button for invite user
  function InviteButton(establishment: Establishment) {
    return (
      <Link
        icon={faUserPlus}
        isWithoutMargin
        onClick={() => {
          dispatch(establishmentStore.actions.setActive(establishment))
          setModal(MODAL_USER_ADD)
        }}>
        {t("Ajouter un contributeur")}
      </Link>
    )
  }

  return (
    <Card isNotCardClass status={status !== STATUS_LOADED ? status : undefined} isWithoutPadding>
      {modal === MODAL_USER_ADD && (
        <AdmEditUserModal
          isSmall
          allowFromSameAccount
          onClose={() => setModal(null)}
          onNext={(user: User) => save(user, "add")}
        />
      )}

      {modal === MODAL_USER_EDIT && (
        <AdmEditUserModal
          isSmall
          userToUpdate={currentUser}
          onClose={() => setModal(null)}
          onNext={(user: User) => save(user, "edit")}
        />
      )}

      {modal === MODAL_SEND_EMAIL && (
        <AdmSendEmailModal
          emailCode="inviteEstablishmentContributor"
          currentUser={currentUser}
          onClose={() => setModal(null)}
          textBold={t(
            `Souhaitez-vous envoyer un email d'invitation à ${currentUser.username} (${currentUser.email}) en tant ${accountRisksSettings.allowMultipleEstablishments ? "que contributeur" : "qu'administrateur"} du document unique pour l'établissement ${establishment.name} ?`
          )}
          items={[
            {
              id: "establishment",
              value: establishment.name
            }
          ]}
        />
      )}

      <div className="mb-4 flex rel items-center">
        <div className="flex items-center gap-3">
          <FontAwesomeIcon icon={faUsers} className="grey-t text-lg" />
          <span className="text-lg">Contributeurs</span>
        </div>
        <Space />
        {InviteButton(establishment)}
      </div>

      {contributors.length === 0 && (
        <div className="grey-t">
          {t(`Aucun contributeur n'a été nommé pour l'établissement ${establishment.name}.`)}
        </div>
      )}

      <div style={{ margin: "0 -30px" }}>
        {contributors.map((user) => (
          <ListItem key={user.id}>
            <ListIcon image={user.imageUrl} fallbackIcon={faUserCircle} />
            {user.firstname} {user.lastname}
            <Space />
            {_session.userId === user.id && (
              <Chip icon={faCrown} className="mr-4">
                {t("utils_self")}
              </Chip>
            )}
            {user.email ? (
              <span className="grey-t">{user.email}</span>
            ) : (
              <span className="orange-t">{t("invitation_no_email")}</span>
            )}
            <div className="w-10" />
            {user.id !== _session.userId && (
              <>
                <SendInvitationButton user={user} onClick={() => clickAction(ACTION_USER_INVITE, user)} />
                <ListButton
                  onAction={(action: string) => clickAction(action, user)}
                  icon={faEllipsisV}
                  actions={getActions()}
                />
              </>
            )}
          </ListItem>
        ))}
      </div>
    </Card>
  )
}
